import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  History,
  ListChecks,
  Package,
  FileCheck,
  Settings,
  Clock,
  ChevronDown,
  ChevronUp,
  Loader2,
  Calendar,
  User,
  AlertTriangle,
} from "lucide-react";
import { format, formatDistanceToNow, isToday, isYesterday } from "date-fns";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import { amsAuditHistoryService } from "../../../services/amsAuditHistoryService";
import type { AuditHistoryEvent } from "../../../services/amsAuditHistoryService";
import { BRAND_COLORS } from "../../../shared/constants/brandColors";

interface AmsAuditHistoryDrawerProps {
  auditId: string;
  isOpen: boolean;
  onClose: () => void;
}

type Category = AuditHistoryEvent["category"];

type ChipKey = "all" | "checkpoint" | "assetsEquipment" | "approval" | "audit";

const FILTER_CHIPS: { key: ChipKey; label: string; categories?: Category[] }[] = [
  { key: "all", label: "All" },
  { key: "checkpoint", label: "Checkpoints", categories: ["checkpoint"] },
  { key: "assetsEquipment", label: "Assets & Equipment", categories: ["asset", "equipment"] },
  { key: "approval", label: "Approvals", categories: ["approval"] },
  { key: "audit", label: "Audit config", categories: ["audit"] },
];

type DayGroup = { dateLabel: string; dateKey: string; events: AuditHistoryEvent[] };

function getCategoryIcon(category: Category) {
  switch (category) {
    case "checkpoint":
      return <ListChecks size={16} className="text-blue-600" />;
    case "asset":
    case "equipment":
      return <Package size={16} className="text-amber-600" />;
    case "approval":
      return <FileCheck size={16} className="text-violet-600" />;
    case "audit":
      return <Settings size={16} className="text-gray-600" />;
    default:
      return <History size={16} className="text-gray-600" />;
  }
}

function getActorName(event: AuditHistoryEvent): string {
  if (event.actorName) return event.actorName;
  if (event.actorId && typeof event.actorId === "object") {
    return event.actorId.name || "";
  }
  return "";
}

// Groups events (already newest-first from the API) by calendar day without re-sorting
// within a day, preserving the API's ordering.
function groupByDay(events: AuditHistoryEvent[]): DayGroup[] {
  const map = new Map<string, AuditHistoryEvent[]>();
  const order: string[] = [];
  for (const event of events) {
    const d = new Date(event.createdAt);
    const key = isNaN(d.getTime()) ? "invalid" : format(d, "yyyy-MM-dd");
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(event);
  }
  return order.map((dateKey) => {
    let dateLabel: string;
    if (dateKey === "invalid") {
      dateLabel = "Unknown date";
    } else {
      const d = new Date(dateKey);
      if (isToday(d)) dateLabel = "Today";
      else if (isYesterday(d)) dateLabel = "Yesterday";
      else dateLabel = format(d, "EEEE, MMM d, yyyy");
    }
    return { dateKey, dateLabel, events: map.get(dateKey) ?? [] };
  });
}

// Renders a changed value readably: strings/numbers/booleans print as-is,
// everything else (arrays, objects) is JSON-stringified for a compact diff line.
function renderChangeValue(value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export default function AmsAuditHistoryDrawer({
  auditId,
  isOpen,
  onClose,
}: AmsAuditHistoryDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<AuditHistoryEvent[]>([]);
  const [activeChip, setActiveChip] = useState<ChipKey>("all");
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpen || !auditId) return;
    setLoading(true);
    setError(null);
    setActiveChip("all");
    setExpandedEvents(new Set());
    amsAuditHistoryService
      .getAuditHistory(auditId, { limit: 200 })
      .then((res) => {
        setEvents(res.data);
      })
      .catch(() => {
        setEvents([]);
        setError("Failed to load audit history.");
      })
      .finally(() => setLoading(false));
  }, [isOpen, auditId]);

  const activeCategories = FILTER_CHIPS.find((c) => c.key === activeChip)?.categories;

  const filteredEvents = useMemo(() => {
    if (!activeCategories) return events;
    return events.filter((e) => activeCategories.includes(e.category));
  }, [events, activeCategories]);

  const dayGroups = useMemo(() => groupByDay(filteredEvents), [filteredEvents]);

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  return (
    <RightDrawer open={isOpen} onClose={onClose} title="" size="xl">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div
          className="flex-shrink-0 px-6 py-4 border-b border-gray-200"
          style={{
            background: `linear-gradient(135deg, ${BRAND_COLORS.primaryLight} 0%, ${BRAND_COLORS.primarySoft} 100%)`,
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center border"
              style={{
                backgroundColor: BRAND_COLORS.primary + "18",
                borderColor: BRAND_COLORS.primaryBorder,
              }}
            >
              <History size={24} style={{ color: BRAND_COLORS.primary }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Audit History</h2>
              <p className="text-sm text-gray-600">Read-only timeline of changes to this audit</p>
            </div>
          </div>
        </div>

        {/* Filter chip bar */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200 bg-white">
          <div className="flex flex-wrap gap-2">
            {FILTER_CHIPS.map((chip) => {
              const isActive = activeChip === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setActiveChip(chip.key)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors"
                  style={
                    isActive
                      ? {
                          backgroundColor: BRAND_COLORS.primary,
                          borderColor: BRAND_COLORS.primary,
                          color: "#fff",
                        }
                      : {
                          backgroundColor: "#fff",
                          borderColor: BRAND_COLORS.neutral[300],
                          color: BRAND_COLORS.neutral[700],
                        }
                  }
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={32} className="animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="text-center py-16 text-red-500">
              <AlertTriangle size={48} className="mx-auto mb-4 opacity-50" />
              <p>{error}</p>
            </div>
          ) : dayGroups.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <History size={48} className="mx-auto mb-4 opacity-50" />
              <p>No history yet</p>
              <p className="text-sm mt-1">Changes to this audit will appear here.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {dayGroups.map((group) => (
                <motion.div
                  key={group.dateKey}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm"
                >
                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-50">
                    <Calendar size={18} className="text-gray-500" />
                    <span className="font-medium text-gray-900">{group.dateLabel}</span>
                    <span className="text-sm text-gray-500">
                      ({group.events.length} {group.events.length === 1 ? "event" : "events"})
                    </span>
                  </div>
                  <div className="divide-y divide-gray-50 border-t border-gray-100">
                    {group.events.map((event) => {
                      const actorName = getActorName(event);
                      const hasChanges = !!event.changes && event.changes.length > 0;
                      const isExpanded = expandedEvents.has(event.id);
                      const createdDate = new Date(event.createdAt);
                      const isValidDate = !isNaN(createdDate.getTime());

                      return (
                        <div key={event.id} className="px-4 py-3 hover:bg-gray-50/50 transition-colors">
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                              {getCategoryIcon(event.category)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900">{event.summary}</p>
                                {hasChanges && (
                                  <button
                                    type="button"
                                    onClick={() => toggleEventExpanded(event.id)}
                                    className="flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                    aria-label={isExpanded ? "Collapse details" : "Expand details"}
                                  >
                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                  </button>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                                {actorName && (
                                  <span className="flex items-center gap-1">
                                    <User size={12} />
                                    {actorName}
                                  </span>
                                )}
                                {isValidDate && (
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {format(createdDate, "h:mm a")}
                                    <span className="text-gray-400">
                                      ({formatDistanceToNow(createdDate, { addSuffix: true })})
                                    </span>
                                  </span>
                                )}
                              </div>

                              {/* Rejection remark — visually highlighted */}
                              {event.remark && (
                                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                                  <p className="text-xs font-semibold text-red-700 mb-0.5">Remark</p>
                                  <p className="text-xs text-red-700 whitespace-pre-wrap break-words">
                                    {event.remark}
                                  </p>
                                </div>
                              )}

                              {/* Expandable field diffs */}
                              <AnimatePresence>
                                {hasChanges && isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden"
                                  >
                                    <div className="mt-2 space-y-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                      {event.changes!.map((change, idx) => (
                                        <div key={`${event.id}-${change.field}-${idx}`} className="text-xs text-gray-700">
                                          <span className="font-semibold text-gray-800">{change.field}</span>
                                          {": "}
                                          <span className="text-gray-500">{renderChangeValue(change.from)}</span>
                                          {" → "}
                                          <span className="font-medium" style={{ color: BRAND_COLORS.primary }}>
                                            {renderChangeValue(change.to)}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </RightDrawer>
  );
}
